import React from 'react';
import { Button, SkeletonText } from 'carbon-components-react';
import {
  Close16,
  Close24,
  Calendar16,
  Construction16,
  List16,
  Download16,
  ArrowLeft16
} from '@carbon/icons-react';
import Utils from 'utilities/helper.util';
import { useLualExtent } from '../useLualExtent';

type Props = {
  page: string;
  parentPage?: string;
  enquiryDetail: any;
  handleGetPlansClick: Function;
  handleBackButtonClick: () => void;
  handleMobileJobDetailsClick: () => void;
  handleMobileDownloadAndToolsClick: () => void;
  isLoading: boolean;
  isModal: boolean;
  onClickClose?: any;
  responsesExpired: boolean;
  showResponsesExpiredModal: boolean;
  setLualExtent: Function;

  isFromSwxLocate: boolean;
};

const ReferenceSection = ({
  enquiryDetail,
  isLoading
}: {
  enquiryDetail: any;
  isLoading: boolean;
}) => (
  <div className="enquiryInformation--details-show-info-reference">
    <div className="mobile-view-enquiry-information__reference">
      {(!isLoading && enquiryDetail?.userReference) || <SkeletonText />}
    </div>
  </div>
);

const ActionSection = ({
  page,
  handleGetPlansClick,
  handleMobileJobDetailsClick,
  handleMobileDownloadAndToolsClick,
  isLoading
}: {
  page: string;
  handleGetPlansClick: Function;
  handleMobileJobDetailsClick: () => void;
  handleMobileDownloadAndToolsClick: () => void;
  isLoading: boolean;
}) => (
  <div className="enquiryInformation--details-show-action">
    {['overview'].includes(page) && (
      <div className="mobile-view-enquiry-information__actions">
        <Button
          onClick={handleMobileJobDetailsClick}
          renderIcon={List16}
          kind={'tertiary'}
          className={
            'toggle-more-details mobile-view-enquiry-information__actions-button'
          }
          disabled={isLoading}
        >
          Job details
        </Button>
        <Button
          kind="tertiary"
          renderIcon={List16}
          onClick={handleGetPlansClick}
          disabled={isLoading}
          className="toggle-more-details mobile-view-enquiry-information__actions-button mobile-view-enquiry-information__actions-button--get-plans"
        >
          View responses
        </Button>
      </div>
    )}

    {['mobile-getPlans'].includes(page) && !isLoading && (
      <div className="mobile-view-enquiry-information__actions">
        <Button
          onClick={handleMobileJobDetailsClick}
          renderIcon={List16}
          kind={'tertiary'}
          className={
            'toggle-more-details mobile-view-enquiry-information__actions-button'
          }
        >
          Job details
        </Button>
        <Button
          onClick={handleMobileDownloadAndToolsClick}
          renderIcon={Download16}
          kind={'tertiary'}
          className={
            'toggle-more-details mobile-view-enquiry-information__actions-button mobile-view-enquiry-information__actions-button--download-tools'
          }
        >
          Download & tools
        </Button>
      </div>
    )}
  </div>
);

const CloseAdditionalSectionButton = ({
  parentPage,
  handleGoToOverviewPage,
  handleGoToGetPlansPage
}: {
  parentPage: string;
  handleGoToOverviewPage: () => void;
  handleGoToGetPlansPage: Function;
}) => (
  <Button
    onClick={() => {
      if (parentPage === 'overview') {
        handleGoToOverviewPage();
      } else if (parentPage === 'mobile-getPlans') {
        handleGoToGetPlansPage();
      }
    }}
    kind={'ghost'}
    className="mobile-view-enquiry-information__back-button"
  >
    <div className="mobile-view-enquiry-information__back-button-content">
      <Close16 className="mobile-view-enquiry-information__back-button-icon" />
      Close
    </div>
  </Button>
);

const BackToOverviewButton = ({
  handleGoToOverviewPage
}: {
  handleGoToOverviewPage: () => void;
}) => (
  <Button
    kind={'ghost'}
    className="mobile-view-enquiry-information__back-button"
    onClick={handleGoToOverviewPage}
  >
    <div className="mobile-view-enquiry-information__back-button-content">
      <ArrowLeft16 className="mobile-view-enquiry-information__back-button-icon" />
      Back
    </div>
  </Button>
);

const CloseModalButton = ({ onClickClose }: { onClickClose: any }) => (
  <div className="close-button-wrapper mobile-view-enquiry-information__close-button-wrapper">
    <div
      className="close-button-container mobile-view-enquiry-information__close-button-container"
      onClick={onClickClose}
    >
      <Close24 />
    </div>
  </div>
);

const NavigationSection = ({
  page,
  parentPage,
  isModal,
  handleGoToOverviewPage,
  handleGoToGetPlansPage,
  onClickClose,
  isFromSwxLocate
}: {
  page: string;
  parentPage: string;
  isModal: boolean;
  handleGoToOverviewPage: () => void;
  handleGoToGetPlansPage: Function;
  onClickClose: () => void;
  isFromSwxLocate: boolean;
}) => {
  return (
    <div
      className={`mobile-view-enquiry-information__navigation ${
        isModal ? 'mobile-view-enquiry-information__navigation--modal' : ''
      }`}
    >
      <div className="mobile-view-enquiry-information__navigation-back-section">
        {['mobile-job-details', 'mobile-download-and-tools'].includes(page) && (
          <CloseAdditionalSectionButton
            parentPage={parentPage}
            handleGoToOverviewPage={handleGoToOverviewPage}
            handleGoToGetPlansPage={handleGoToGetPlansPage}
          />
        )}
        {page === 'mobile-getPlans' && !isFromSwxLocate && (
          <BackToOverviewButton
            handleGoToOverviewPage={handleGoToOverviewPage}
          />
        )}
        {/* A hack to make the back button invisible when on the overview page */}
        {/* This is done to prevent layout shift when switching between pages */}
        {page === 'overview' && (
          <div className="mobile-view-enquiry-information__back-button--invisible">
            <BackToOverviewButton
              handleGoToOverviewPage={handleGoToOverviewPage}
            />
          </div>
        )}
      </div>
      <div className="mobile-view-enquiry-information__navigation-title">
        {parentPage === 'overview' ? 'Overview' : 'Responses'}
      </div>
      <div
        className={`mobile-view-enquiry-information__navigation-close-section ${
          !isModal
            ? 'mobile-view-enquiry-information__navigation-close-section--hidden'
            : ''
        }`}
      >
        <CloseModalButton onClickClose={onClickClose} />
      </div>
    </div>
  );
};

const EnquiryMainDetails = ({
  renderIcon,
  value,
  isLoading
}: {
  renderIcon: React.ReactNode;
  value: React.ReactNode;
  isLoading: boolean;
}) => {
  return !isLoading ? (
    <div className="mobile-view-enquiry-information__main-details">
      {renderIcon}
      {value}
    </div>
  ) : (
    <SkeletonText />
  );
};

const MobileView: React.FC<Props> = ({
  page,
  parentPage,
  enquiryDetail,
  handleGetPlansClick,
  handleBackButtonClick,
  handleMobileJobDetailsClick,
  handleMobileDownloadAndToolsClick,
  isLoading,
  isModal,
  onClickClose,
  responsesExpired,
  showResponsesExpiredModal,
  setLualExtent,

  isFromSwxLocate
}) => {
  useLualExtent(enquiryDetail?.shape, setLualExtent);

  return (
    <div
      className={`enquiryInformation mobile-view-enquiry-information ${
        isFromSwxLocate ? 'locate-theme' : ''
      }`}
    >
      <NavigationSection
        page={page}
        parentPage={parentPage || 'overview'}
        isModal={isModal}
        handleGoToOverviewPage={handleBackButtonClick}
        handleGoToGetPlansPage={handleGetPlansClick}
        onClickClose={onClickClose}
        isFromSwxLocate={isFromSwxLocate}
      />

      <div className="enquiryInformation--details mobile-view-enquiry-information__details">
        {(!responsesExpired || !showResponsesExpiredModal) && (
          <>
            <div
              className={`mobile-view-enquiry-information__info-section-container enquiryInformation--details-show`}
            >
              <div className="enquiryInformation--details-show-info mobile-view-enquiry-information__info-section">
                <ReferenceSection
                  enquiryDetail={enquiryDetail}
                  isLoading={isLoading}
                />

                <EnquiryMainDetails
                  isLoading={isLoading || !enquiryDetail}
                  renderIcon={<Construction16 />}
                  value={
                    enquiryDetail && <div>Job {enquiryDetail?.externalId}</div>
                  }
                />

                <EnquiryMainDetails
                  isLoading={isLoading || !enquiryDetail}
                  renderIcon={<Calendar16 />}
                  value={
                    enquiryDetail && (
                      <div>
                        {Utils.toLocalDateFormat(enquiryDetail?.digStartAt)}{' '}
                        <span className="arrow-symbol">&#8594;</span>{' '}
                        {Utils.toLocalDateFormat(enquiryDetail?.digEndAt)}
                      </div>
                    )
                  }
                />
              </div>

              <ActionSection
                page={page}
                handleGetPlansClick={handleGetPlansClick}
                handleMobileJobDetailsClick={handleMobileJobDetailsClick}
                handleMobileDownloadAndToolsClick={
                  handleMobileDownloadAndToolsClick
                }
                isLoading={isLoading}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileView;
